import {
  Button,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import companyApi from 'api/auth/company/company-api';
import flockApi from 'api/query/flock/flock-api';
import { FlockProfileGenomicProviderModel, FlockProfileTestListModel } from 'api/query/flock/models';
import Axios from 'axios';
import LoadingAlert from 'components/loading-alert';
import Table from 'components/table';
import { Column } from 'components/table/column';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigation } from 'react-navi';
import { emailRegex } from 'utils/validation';

const flockProfileHeaders = [
  'Profile ID',
  'Order ID',
  'Company',
  'Drop Year',
  'Analysis',
  'Analysis Date',
  'Action',
];

type FlockProfilesListProps = {
  companyId?: number;
  companyName?: string;
  returnToPath?: string;
};

type FlockProfileListRow = {
  id: number;
  analysisType: string;
  analysisDate: string;
  orderId: string;
  companyName: string;
  dropYearLabel: string;
};

const formatDate = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getFlockProfileTableHeaders = (): Column[] => (
  flockProfileHeaders.map((header) => ({
    id: header,
    label: header,
    align: 'left',
    enableSort: false,
    isSorted: false,
    sortDir: undefined,
    hasInfo: false,
    isSticky: false,
  }))
);

const getDistinctStrings = (values: string[]): string[] => (
  Array.from(new Set(values.filter((value) => value.length > 0)))
    .sort((first, second) => first.localeCompare(second))
);

const mapFlockProfileListRow = (profile: FlockProfileTestListModel): FlockProfileListRow => ({
  id: profile.id,
  analysisType: profile.analysisType,
  analysisDate: profile.analysisDate,
  orderId: profile.orderId,
  companyName: profile.companyName,
  dropYearLabel: profile.dropYearLabel || '',
});

const normaliseSearchValue = (value: string | number): string => (
  String(value).toLocaleLowerCase()
);

const normaliseOrderCodeInput = (value: string): string => (
  value.replace(/[^A-Za-z0-9]/g, '').slice(0, 6)
);

const genericClaimFailureMessage = 'We could not find a matching Flock Profile order. Please check the provider, 6-character order code and email address supplied with the order.';

const matchesSearch = (profile: FlockProfileListRow, search: string): boolean => {
  const searchTerm = search.trim().toLocaleLowerCase();

  if (searchTerm.length === 0) {
    return true;
  }

  return [
    profile.id,
    profile.orderId,
    profile.companyName,
    profile.analysisType,
    profile.dropYearLabel,
  ].some((value) => normaliseSearchValue(value).includes(searchTerm));
};

export const FlockProfilesList: React.FC<FlockProfilesListProps> = ({
  companyId: routeCompanyId,
  companyName,
  returnToPath = '/flock-profile',
}) => {
  const navigation = useNavigation();
  const headers = useMemo(() => getFlockProfileTableHeaders(), []);
  const [allRows, setAllRows] = useState<FlockProfileListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dropYear, setDropYear] = useState('');
  const [analysisName, setAnalysisName] = useState('');
  const [claimCompanyId, setClaimCompanyId] = useState<number | null>(null);
  const [providers, setProviders] = useState<FlockProfileGenomicProviderModel[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState<string | null>(null);
  const [providerCode, setProviderCode] = useState('');
  const [orderCodeInput, setOrderCodeInput] = useState('');
  const [orderEmailInput, setOrderEmailInput] = useState('');
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimSuccess, setClaimSuccess] = useState<string | null>(null);
  const isAdminCompanyContext = Number.isInteger(routeCompanyId) && Number(routeCompanyId) > 0;

  const rows = useMemo(() => (
    allRows.filter((profile) => (
      matchesSearch(profile, search)
      && (dropYear.length === 0 || profile.dropYearLabel === dropYear)
      && (analysisName.length === 0 || profile.analysisType === analysisName)
    ))
  ), [allRows, analysisName, dropYear, search]);

  const dropYearOptions = useMemo(
    () => getDistinctStrings(allRows.map((profile) => profile.dropYearLabel)),
    [allRows],
  );
  const analysisNameOptions = useMemo(
    () => getDistinctStrings(allRows.map((profile) => profile.analysisType)),
    [allRows],
  );
  const emailHasError = orderEmailInput.length > 0 && !emailRegex.test(orderEmailInput.trim());
  const orderCodeHasError = orderCodeInput.length > 0 && orderCodeInput.length !== 6;
  const claimDisabled =
    claimSubmitting
    || providersLoading
    || !claimCompanyId
    || !providerCode
    || orderCodeInput.length !== 6
    || !orderEmailInput.trim()
    || orderCodeHasError
    || emailHasError;

  const refreshFlockProfileRows = useCallback(async (
    token?: ReturnType<typeof Axios.CancelToken.source>['token'],
  ) => {
    setLoading(true);
    setError(null);

    try {
      const results = isAdminCompanyContext
        ? await flockApi.getAdminCompanyFlockProfileTests(Number(routeCompanyId), token)
        : await flockApi.getFlockProfileTests(token);
      setAllRows(results.map(mapFlockProfileListRow));
    } catch (requestError) {
      if (!Axios.isCancel(requestError)) {
        setAllRows([]);
        setError('Unable to load Flock Profiles. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [isAdminCompanyContext, routeCompanyId]);

  const refreshProviders = useCallback(async (
    token?: ReturnType<typeof Axios.CancelToken.source>['token'],
  ) => {
    setProvidersLoading(true);
    setProvidersError(null);

    try {
      const results = await flockApi.getFlockProfileOrderClaimProviders(token);
      setProviders(results);
    } catch (requestError) {
      if (!Axios.isCancel(requestError)) {
        setProviders([]);
        setProvidersError('Unable to load genomic providers. Please try again.');
      }
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    let cancelled = false;

    const loadInitialData = async () => {
      try {
        if (isAdminCompanyContext) {
          setClaimCompanyId(Number(routeCompanyId));
          await Promise.all([
            refreshFlockProfileRows(source.token),
            refreshProviders(source.token),
          ]);
          return;
        }

        const [detail] = await Promise.all([
          companyApi.getMyCompanyDetail(true, source.token),
          refreshFlockProfileRows(source.token),
          refreshProviders(source.token),
        ]);

        if (cancelled) {
          return;
        }

        const resolvedCompanyId = detail?.company?.id ?? null;
        setClaimCompanyId(resolvedCompanyId);
      } catch (requestError) {
        if (!cancelled && !Axios.isCancel(requestError)) {
          setClaimCompanyId(null);
          setClaimError('Unable to load the company for Flock Profile order claiming.');
        }
      }
    };

    void loadInitialData();

    return () => {
      cancelled = true;
      source.cancel('Flock Profiles list unmounted.');
    };
  }, [isAdminCompanyContext, refreshFlockProfileRows, refreshProviders, routeCompanyId]);

  const handleViewProfile = (flockProfileId: number) => {
    const detailPath = isAdminCompanyContext
      ? `/flock-profiles/${flockProfileId}`
      : `/flock-profiles/${flockProfileId}`;

    navigation.navigate(detailPath, {
      state: {
        companyId: routeCompanyId,
        returnTo: returnToPath,
      },
    });
  };

  const updateSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const updateDropYear = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    setDropYear(typeof value === 'string' ? value : '');
  };

  const updateAnalysisName = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    setAnalysisName(typeof value === 'string' ? value : '');
  };

  const updateOrderCodeInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setOrderCodeInput(normaliseOrderCodeInput(event.target.value));
    setClaimError(null);
    setClaimSuccess(null);
  };

  const updateOrderEmailInput = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setOrderEmailInput(event.target.value);
    setClaimError(null);
    setClaimSuccess(null);
  };

  const claimFlockProfileOrder = async () => {
    if (claimDisabled || !claimCompanyId) {
      return;
    }

    setClaimSubmitting(true);
    setClaimError(null);
    setClaimSuccess(null);

    try {
      await flockApi.claimFlockProfileOrder(claimCompanyId, {
        genomicProviderCompanyCode: providerCode,
        orderCode: orderCodeInput.trim(),
        email: orderEmailInput.trim(),
      });
      setProviderCode('');
      setOrderCodeInput('');
      setOrderEmailInput('');
      setClaimSuccess(isAdminCompanyContext
        ? `Flock Profile order claimed successfully for ${companyName || `company ${claimCompanyId}`}.`
        : 'Flock Profile order claimed successfully.');
      await refreshFlockProfileRows();
    } catch (requestError) {
      setClaimError(genericClaimFailureMessage);
    } finally {
      setClaimSubmitting(false);
    }
  };

  const renderClaimOrder = () => {
    if (!claimCompanyId) {
      return null;
    }

    return (
      <Card className='flock-profiles__claim-order'>
        <div className='flock-profiles__claim-order-header'>
          <div>
            <h2 className='flock-profiles__claim-order-title'>Claim Flock Profile order</h2>
            <p className='flock-profiles__claim-order-copy'>
              {isAdminCompanyContext
                ? `You are claiming this Flock Profile order for ${companyName || `company ${claimCompanyId}`}. Enter the genomic provider, 6-character order code, and the email address supplied when the order was placed.`
                : 'To claim a Flock Profile order, enter the genomic provider, 6-character order code, and the email address supplied when the order was placed.'}
            </p>
          </div>
        </div>

        {providersError && <Alert severity='error'>{providersError}</Alert>}
        {claimError && <Alert severity='error'>{claimError}</Alert>}
        {claimSuccess && <Alert severity='success'>{claimSuccess}</Alert>}
        {providersLoading ? (
          <LoadingAlert message='Loading genomic providers…' />
        ) : (
          <div className='flock-profiles__claim-order-form'>
            <FormControl variant='outlined' size='small' className='flock-profiles__claim-order-field'>
              <label className='MuiInputLabel-root'>Genomic Provider</label>
              <Select
                value={providerCode}
                onChange={(event) => {
                  setProviderCode(typeof event.target.value === 'string' ? event.target.value : '');
                  setClaimError(null);
                  setClaimSuccess(null);
                }}
                inputProps={{ 'aria-label': 'Genomic Provider' }}
                displayEmpty
              >
                <MenuItem value=''>Select provider</MenuItem>
                {providers.map((provider) => (
                  <MenuItem key={provider.companyCode} value={provider.companyCode}>
                    {provider.companyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              className='flock-profiles__claim-order-field'
              label='Order Code'
              variant='outlined'
              size='small'
              placeholder='Enter Order Code'
              value={orderCodeInput}
              onChange={updateOrderCodeInput}
              inputProps={{
                inputMode: 'text',
                maxLength: 6,
                pattern: '[A-Za-z0-9]*',
              }}
              helperText={orderCodeHasError ? 'Enter the 6-character code supplied with the order.' : 'Use the 6-character code supplied with the order.'}
              error={orderCodeHasError}
            />
            <TextField
              className='flock-profiles__claim-order-field'
              label='Order Email Address'
              variant='outlined'
              size='small'
              type='email'
              value={orderEmailInput}
              onChange={updateOrderEmailInput}
              placeholder='Enter email address'
              helperText={emailHasError ? 'Enter a valid email address.' : 'Use the email address supplied when the order was placed.'}
              error={emailHasError}
            />
            <Button
              variant='contained'
              color='primary'
              style={{ marginTop: '20px' }}
              startIcon={claimSubmitting ? <CircularProgress color='inherit' size={16} /> : <Add />}
              onClick={claimFlockProfileOrder}
              disabled={claimDisabled}
            >
              Claim order
            </Button>
          </div>
        )}
      </Card>
    );
  };

  const renderTableContent = () => {
    if (loading) {
      return <LoadingAlert message='Loading Flock Profiles…' />;
    }

    if (error) {
      return <Alert severity='error'>{error}</Alert>;
    }

    if (rows.length === 0) {
      return <Alert severity='info'>No flock profiles available.</Alert>;
    }

    return (
      <Table
        className='flock-profiles__table'
        columns={headers}
        resultsCount={rows.length}
        totalCount={rows.length}
        loadedNoun='Flock Profiles'
        loading={false}
        error={false}
        showLoadMore={false}
        isVerticallyScrolled={false}
        scrollShadow={false}
        stickyHeader={false}
        loadingMore={false}
      >
        {rows.map((profile) => (
          <React.Fragment key={profile.id}>
            <TableRow
              className='flock-profiles__row'
              id={`row-${profile.id}`}
              tabIndex={-1}
            >
              <TableCell className='flock-profiles__cell flock-profiles__cell--bold'>
                {profile.id}
              </TableCell>
              <TableCell className='flock-profiles__cell'>
                {profile.orderId}
              </TableCell>
              <TableCell className='flock-profiles__cell'>
                {profile.companyName}
              </TableCell>
              <TableCell className='flock-profiles__cell'>
                {profile.dropYearLabel || '-'}
              </TableCell>
              <TableCell className='flock-profiles__cell'>
                {profile.analysisType}
              </TableCell>
              <TableCell className='flock-profiles__cell'>
                {formatDate(profile.analysisDate)}
              </TableCell>
              <TableCell className='flock-profiles__cell flock-profiles__cell--actions'>
                <Button
                  variant='text'
                  color='primary'
                  className='flock-profiles__action-button'
                  // startIcon={<Visibility />}
                  onClick={() => handleViewProfile(profile.id)}
                >
                  View Profile
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={headers.length} className='flock-profiles__divider-cell'>
                <Divider variant='fullWidth' />
              </TableCell>
            </TableRow>
          </React.Fragment>
        ))}
      </Table>
    );
  };

  return (
    <>
      {allRows.length > 0 && (
        <Card className='flock-profiles__table-container'>
          <div className='flock-profiles__filters'>
            <TextField
              className='flock-profiles__filter flock-profiles__filter--search'
              label='Search'
              placeholder='Search by profile, order, company, analysis or drop year'
              variant='outlined'
              size='small'
              value={search}
              onChange={updateSearch}
            />
            <FormControl variant='outlined' size='small' className='flock-profiles__filter'>
              <label className='MuiInputLabel-root'>Drop Year</label>
              <Select
                value={dropYear}
                onChange={updateDropYear}
                inputProps={{ 'aria-label': 'Drop Year' }}
                displayEmpty
              >
                <MenuItem value=''>All</MenuItem>
                {dropYearOptions.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant='outlined' size='small' className='flock-profiles__filter'>
              <label className='MuiInputLabel-root'>Analysis</label>
              <Select
                value={analysisName}
                onChange={updateAnalysisName}
                inputProps={{ 'aria-label': 'Analysis' }}
                displayEmpty
              >
                <MenuItem value=''>All</MenuItem>
                {analysisNameOptions.map((analysis) => (
                  <MenuItem key={analysis} value={analysis}>{analysis}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Card>
      )}

      <Card>
        <div className='flock-profiles__table-container'>
          {renderTableContent()}
        </div>
      </Card>

      {!loading && !error && allRows.length === 0 && (
        <Card>
          <div className='flock-profiles__table-container'>
            <Alert severity='info'>
              Claim a Flock Profile order below and matching reports will appear here once they are linked to this company.
            </Alert>
          </div>
        </Card>
      )}

      {renderClaimOrder()}
    </>
  );
};

export default FlockProfilesList;
